'use client';

import React, { useEffect, useState } from 'react';
import Flights from '@/components/flights';
import { useRouter } from '@/lib/navigation';
import FareSummary from '@/components/fare-summary/fare-summary';
import { formatCurrency, getDataForCartApi } from '@/lib/utils';
import { useDispatch, useSelector } from 'react-redux';
import { useAddToCartMutation } from '@flysync/shared-redux/services/booking';
import {
  updateFareDetails,
  updateReviewData,
  updateCartId,
  clearLocalState,
} from '@flysync/shared-redux/slices/flightSearchSlice';
import { format } from 'date-fns';
import { setSelectedBucket } from '@flysync/shared-redux/slices/userInputSlice';
import { useToast } from '@/hooks/use-toast';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';

function FlightDetails() {
  const userInput = useSelector((state: any) => state.userInputs);
  const addToCartApiCalled = React.useRef(false);
  const stateData = useSelector((state: any) => state.flightSearch);
  const { selectedFlight, selectedBucket, selectedReturnFlight } = userInput;
  const [addToCart, { isLoading }] = useAddToCartMutation();
  const router = useRouter();
  const dispatch = useDispatch();
  const { toast } = useToast();
  const [showFareCheck, setShowFareCheck] = useState(false);
  const [fareCheckData, setFareCheckData] = useState<{
    oldAmount: number;
    newAmount: number;
    fareDetails: any;
    bookingId: string;
  } | null>(null);

  const callback = () => {
    router.push('/traveler-info');
  };

  useEffect(() => {
    const reqData = JSON.parse(localStorage.getItem('flightData') || '{}');
    if (Object?.keys(reqData)?.length > 0) {
      reqData.trip_type = reqData.travelMode;
      sessionStorage.setItem('reqData', JSON.stringify(reqData));
      dispatch(updateReviewData(reqData));
    }
    localStorage.removeItem('flightData');
  }, []);

  const callAddToCartApi = async () => {
    const outBoundData = (getDataForCartApi(
      selectedFlight,
      selectedFlight?.price_details?.[0],
    ) as {
      data: never[];
      fareRules: never[];
      fare_details: never[];
      priceIds: never[];
      trip_type: string;
      product_type: string;
    }) || {
      data: [],
      fareRules: [],
      fare_details: [],
      priceIds: [],
      trip_type: '',
      product_type: 'B2C',
    };
    const returnData = (getDataForCartApi(
      selectedReturnFlight,
      selectedReturnFlight?.price_details?.[0],
    ) as {
      data: never[];
      fareRules: never[];
      fare_details: never[];
      priceIds: never[];
      trip_type: string;
    }) || {
      data: [],
      fareRules: [],
      fare_details: [],
      priceIds: [],
      trip_type: '',
    };
    if (Object?.keys(selectedFlight)?.length > 0) {
      interface FlightData {
        outBoundData: {
          data: any[];
          fareRules: any[];
          fare_details: any[];
          priceIds: any[];
          trip_type: string;
          product_type: string;
        };
        returnData: {
          data: any[];
          fareRules: any[];
          fare_details: any[];
          priceIds: any[];
        };
      }

      let flightData: FlightData = {
        outBoundData: outBoundData,
        returnData:
          stateData.travelType === 'RT' && stateData.travelMode === 'DO'
            ? returnData
            : {
                data: [],
                fareRules: [],
                fare_details: [],
                priceIds: [],
              },
      };

      const updateFlightData = (
        data: Array<{ passenger: any }>,
        flight: { price_details: any[] },
        fareRules: any[],
        departureDate: string,
      ) => {
        return data.map((item, index) => ({
          ...item,
          price_details: {
            ...flight.price_details[0],
            specifications: {
              ...fareRules[index],
              passengers: stateData.passengers,
            },
          },
          departureDate: format(new Date(departureDate), 'yyyy-MM-dd'),
        }));
      };

      flightData.outBoundData.data = updateFlightData(
        outBoundData.data,
        selectedFlight,
        outBoundData.fareRules,
        selectedFlight.departure_date,
      );

      if (stateData.travelType === 'RT' && stateData.travelMode === 'DO') {
        flightData.returnData.data = updateFlightData(
          returnData.data,
          selectedReturnFlight,
          returnData.fareRules,
          selectedReturnFlight.departure_date,
        );
      }

      let params: any = {
        data: [
          ...flightData.outBoundData.data,
          ...(flightData.returnData.data || []),
        ],
        fareRules: [
          ...flightData.outBoundData.fareRules,
          ...(flightData.returnData.fareRules || []),
        ],
        fare_details: [
          ...flightData.outBoundData.fare_details,
          ...(flightData.returnData.fare_details || []),
        ],
        priceIds: [
          ...(flightData.outBoundData.priceIds || []),
          ...(flightData.returnData.priceIds || []),
        ],
        trip_type: flightData.outBoundData.trip_type,
        product_type: flightData.outBoundData.product_type,
      };

      // params.data.map((item: any, index: number) => {
      //   item.price_details.specifications = params.fareRules[index];
      //   item.passenger = params.passengers;
      //   item.departureDate = format(
      //     new Date(selectedFlight.departure_date),
      //     'yyyy-MM-dd',
      //   );
      // });

      try {
        const cartData = await addToCart({
          data: params,
          auth: sessionStorage.getItem('token') || '',
        }).unwrap();

        if (cartData?.code === 200) {
          const { fareIncrease, booking_id, fare_details, otherInfo } =
            cartData?.data;

          otherInfo.map((_data: any, index: number) => {
            params.data[index]['price_details']['otherInfo'] = otherInfo[index];
          });

          params['trip_type'] = params.data[0]?.trip_type || 'DO';

          dispatch(updateReviewData(params));

          // Check if fare has changed
          if (fareIncrease) {
            // Show fare check dialog
            const fareDetails =  {
                ...fare_details,
                ...cartData?.data?.fareCheckInfo[0]?.price_details?.fareDetails,
              }
            const managementFeeWgst = Number(fareDetails?.management_fee || 0) + Number(fareDetails?.management_fee_tax || 0);            
            setFareCheckData({
              oldAmount: Math.abs(Number(fareDetails?.oldtotalAmount) - managementFeeWgst),
              newAmount: Math.abs(Number(fareDetails?.totalAmount) - managementFeeWgst),
              fareDetails: fareDetails,
              bookingId: booking_id,
            });
            setShowFareCheck(true);
            return; // Stop here and wait for user confirmation
          }
          // If no fare change, proceed normally
          proceedWithBooking(fare_details, booking_id);
        } else {
          toast({
            title: '',
            description: cartData?.message,
            variant: 'error',
          });
          router.push('/flight-list');
        }

        dispatch(updateReviewData(params));
      } catch (error) {
        toast({
          title: '',
          description: 'An error occurred while adding to cart',
          variant: 'error',
        });
        router.push('/flight-list');
      }
    }
  };

  // Function to handle proceeding with booking
  const proceedWithBooking = (fareDetails: any, bookingId: string) => {
    const updatedBucket = {
      ...selectedBucket,
      fareDetails: fareDetails,
    };
    dispatch(setSelectedBucket(updatedBucket));
    dispatch(updateFareDetails(fareDetails));
    dispatch(updateCartId(bookingId));
    // router.push('/traveler-info');
  };

  useEffect(() => {
    if (addToCartApiCalled.current == false) {
      addToCartApiCalled.current = true;
      // localStorage.removeItem('flightData');
      //userInput.selectedBucket !== null ? userInput?.selectedBucket : userInput?.selectedFlight?.price_details?.[0])
      callAddToCartApi();
    }
    if (stateData?.tripType != 'RT') {
      clearLocalState(selectedReturnFlight);
    }
  }, [userInput, stateData]);
  // useEffect(() => {
  //   if (isSuccess && bookTicketData?.data?.data?.booking_id) {
  //     addToCart({
  //       data: { booking_id: bookTicketData?.data?.data?.booking_id },
  //     });
  //   }
  // }, [isSuccess, bookTicketData]);

  return (
    <>
      <div className="bg-tertiary">
        <div className="relative">
          <div className="h-14 w-full sm:h-20 lg:h-48" />
          <div className="absolute inset-x-0 top-3 mx-auto px-1 pb-4 sm:top-6 sm:w-[1200px] sm:pb-8 lg:top-12 lg:pb-20">
            <div className="mb-3 flex items-center gap-2 sm:mb-5 lg:mb-7">
              <h2 className="pl-2 text-base font-semibold text-white sm:pl-4 sm:text-lg lg:pl-8 lg:text-xl">
                Flight Details
              </h2>
            </div>
            <div className="flex w-full flex-col-reverse gap-4 lg:flex-row">
              <div className="order-2 w-full lg:order-1 lg:w-[75%]">
                <div className="rounded-lg bg-white p-4 shadow-md sm:rounded-xl sm:p-6 sm:shadow-lg lg:p-8">
                  <div className="mb-6">
                    <Flights data={selectedFlight} type="OUT_BOUND" />
                  </div>
                  {selectedReturnFlight && stateData.travelType === 'RT' && (
                    <div className="mt-4 border-t border-gray-100 pt-4 sm:mt-6 sm:pt-6">
                      <Flights data={selectedReturnFlight} type="RETURN" />
                    </div>
                  )}
                </div>
              </div>

              {/* Fare Summary Section */}
              <div className="sticky top-4 order-1 w-full lg:order-2 lg:w-[25%]">
                <div className="flex flex-col gap-4 sm:gap-5">
                  <FareSummary
                    summary={selectedBucket}
                    isLoader={isLoading}
                    addOns={{ isShow: false }}
                    callback={callback}
                    className="rounded-lg bg-white p-4 shadow-md sm:rounded-xl sm:p-6 sm:shadow-lg"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Loading Overlay
      {isLoading && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-4 flex items-center gap-3">
            <Loader2 className="h-5 w-5 animate-spin text-primary" />
            <span className="text-sm font-medium">Loading details...</span>
          </div>
        </div>
      )} */}

      {/* Fare Check Dialog */}
      <Dialog open={showFareCheck} onOpenChange={setShowFareCheck}>
        <DialogContent className="w-[95%] max-w-md gap-4 rounded-lg p-4 sm:w-[450px] sm:gap-6 sm:rounded-xl sm:p-6">
          <DialogHeader className="space-y-2 sm:space-y-3">
            <DialogTitle className="flex items-center gap-2 text-lg font-semibold text-amber-500 sm:text-xl">
              <AlertTriangle className="h-4 w-4 sm:h-5 sm:w-5" />
              Fare Change Alert
            </DialogTitle>
            <DialogDescription className="text-sm leading-normal text-gray-600 sm:text-base">
              The fare for your selected flight has changed.
            </DialogDescription>
          </DialogHeader>

          <div className="py-3 sm:py-4">
            <div className="space-y-3 sm:space-y-4">
              {/* Previous Fare */}
              <div className="flex items-center justify-between rounded-lg bg-gray-50 px-3 py-2">
                <span className="text-xs text-gray-600 sm:text-sm">
                  Previous Fare
                </span>
                <span className="text-sm font-medium text-gray-500 line-through sm:text-base">
                  {formatCurrency(fareCheckData?.oldAmount || 0)}
                </span>
              </div>

              {/* New Fare */}
              <div className="bg-primary/5 flex items-center justify-between rounded-lg px-3 py-2">
                <span className="text-xs text-gray-600 sm:text-sm">
                  New Fare
                </span>
                <span className="text-sm font-semibold text-primary sm:text-base">
                  {formatCurrency(fareCheckData?.newAmount || 0)}
                </span>
              </div>

              {/* Difference */}
              <div className="mt-2 flex items-center justify-between border-t border-gray-200 pt-3">
                <span className="text-xs font-medium sm:text-sm">
                  Difference
                </span>
                <div
                  className={`flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold sm:text-sm ${
                    (fareCheckData?.newAmount || 0) >
                    (fareCheckData?.oldAmount || 0)
                      ? 'bg-red-50 text-red-600'
                      : 'bg-green-50 text-green-600'
                  } `}
                >
                  {formatCurrency(
                    Math.abs(
                      (fareCheckData?.newAmount || 0) -
                        (fareCheckData?.oldAmount || 0),
                    ),
                  )}
                  <span className="text-xs">
                    {(fareCheckData?.newAmount || 0) >
                    (fareCheckData?.oldAmount || 0)
                      ? '↑ increase'
                      : '↓ decrease'}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <DialogFooter className="mt-2 flex flex-col gap-2 sm:mt-4 sm:flex-row sm:gap-3">
            <Button
              variant="outline"
              onClick={() => {
                setShowFareCheck(false);
                router.push('/flight-list');
              }}
              className="order-2 w-full px-4 py-2 text-sm sm:order-1 sm:w-auto"
            >
              Search Again
            </Button>
            <Button
              onClick={() => {
                if (fareCheckData) {
                  proceedWithBooking(
                    fareCheckData.fareDetails,
                    fareCheckData.bookingId,
                  );
                }
                setShowFareCheck(false);
              }}
              className="hover:bg-primary/90 order-1 w-full bg-primary px-4 py-2 text-sm text-white sm:order-2 sm:w-auto"
            >
              Proceed with New Fare
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default FlightDetails;
